import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Key, Plus, Trash2, Check, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SettingsLayout } from './SettingsLayout';
import { getSSHKeys, addSSHKey, deleteSSHKey, setDefaultSSHKey } from '@/api/settings';
import type { SSHKey } from '@/types/settings';

export function SSHKeysPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteKeyId, setDeleteKeyId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: getSSHKeys,
  });

  const addMutation = useMutation({
    mutationFn: addSSHKey,
    onSuccess: (result) => {
      if (result.success) {
        setSuccess(result.message || 'SSH key added successfully!');
        setName('');
        setPublicKey('');
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to add SSH key');
      }
    },
    onError: () => {
      setError('Failed to add SSH key');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSSHKey,
    onSuccess: (result) => {
      if (result.success) {
        setSuccess('SSH key deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to delete SSH key');
      }
      setDeleteKeyId(null);
    },
    onError: () => {
      setError('Failed to delete SSH key');
      setDeleteKeyId(null);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: setDefaultSSHKey,
    onSuccess: (result) => {
      if (result.success) {
        setSuccess(result.message || 'Default key updated');
        queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to set default key');
      }
    },
    onError: () => {
      setError('Failed to set default key');
    },
  });

  const handleAddKey = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    addMutation.mutate({ name, public_key: publicKey });
  };

  const handleDeleteKey = (keyId: number) => {
    setError(null);
    setSuccess(null);
    deleteMutation.mutate(keyId);
  };

  const handleSetDefault = (keyId: number) => {
    setError(null);
    setSuccess(null);
    setDefaultMutation.mutate(keyId);
  };

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const sshKeys = data?.ssh_keys || [];

  return (
    <SettingsLayout>
      <div className="space-y-6">
        {/* Add New Key Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              SSH Keys
            </CardTitle>
            <CardDescription>
              Manage SSH keys for Remote IDE access to your deployments.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleAddKey}>
            <CardContent className="space-y-4">
              {success && (
                <Alert className="bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800 ml-2">
                    {success}
                  </AlertDescription>
                </Alert>
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="ml-2">
                  Add your SSH public keys to enable Remote IDE access. These keys work across all your projects.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="name">Key Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="e.g., MacBook Pro, Work Laptop"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Give this key a descriptive name to identify it
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="publicKey">Public Key</Label>
                <Textarea
                  id="publicKey"
                  className="font-mono text-sm"
                  rows={4}
                  placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAAB..."
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Paste your SSH public key here. You can get it by running:{' '}
                  <code className="bg-gray-100 px-1 rounded">cat ~/.ssh/id_rsa.pub</code> or{' '}
                  <code className="bg-gray-100 px-1 rounded">cat ~/.ssh/id_ed25519.pub</code>
                </p>
              </div>
            </CardContent>
            <CardFooter className="pt-4">
              <Button type="submit" disabled={addMutation.isPending}>
                {addMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add SSH Key
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>

        {/* Existing Keys Card */}
        <Card>
          <CardHeader>
            <CardTitle>Your SSH Keys</CardTitle>
          </CardHeader>
          <CardContent>
            {sshKeys.length === 0 ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="ml-2">
                  You don't have any SSH keys yet. Add one above to get started.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {sshKeys.map((key: SSHKey) => (
                  <div
                    key={key.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{key.name}</p>
                        {key.is_default && (
                          <Badge className="bg-green-100 text-green-700">Default</Badge>
                        )}
                        <Badge variant="secondary">{key.key_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground font-mono truncate">
                        {key.fingerprint || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {!key.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(key.id)}
                          disabled={setDefaultMutation.isPending}
                        >
                          Set as Default
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteKeyId(key.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteKeyId !== null} onOpenChange={() => setDeleteKeyId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SSH Key?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this SSH key? You will no longer be able to use it for Remote IDE access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteKeyId && handleDeleteKey(deleteKeyId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
}
